import React, { useState, useEffect } from 'react';
import { AlertTriangle, Clock, X } from 'lucide-react';
import { Button } from '../ui/Button';
import { useAuthStore } from '../../store/authStore';
import { AccountDeletionStatusResponse } from '../../types';

interface AccountDeletionBannerProps {
  onCancelDeletion?: () => void;
}

export const AccountDeletionBanner: React.FC<AccountDeletionBannerProps> = ({
  onCancelDeletion,
}) => {
  const { getAccountDeletionStatus, cancelAccountDeletion, isLoading } = useAuthStore();
  const [deletionStatus, setDeletionStatus] = useState<AccountDeletionStatusResponse | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  const [isCancelling, setIsCancelling] = useState(false);

  useEffect(() => {
    const checkDeletionStatus = async () => {
      try {
        const status = await getAccountDeletionStatus();
        setDeletionStatus(status);
        setIsVisible(status.isPendingDeletion);
      } catch (error) {
        console.error('Failed to check deletion status:', error);
      }
    };

    checkDeletionStatus();
  }, [getAccountDeletionStatus]);

  const handleCancelDeletion = async () => {
    setIsCancelling(true);
    try {
      const response = await cancelAccountDeletion();
      if (response.success) {
        setIsVisible(false);
        setDeletionStatus(null);
        if (onCancelDeletion) {
          onCancelDeletion();
        }
      }
    } catch (error) {
      console.error('Failed to cancel deletion:', error);
    } finally {
      setIsCancelling(false);
    }
  };

  const handleDismiss = () => {
    setIsVisible(false);
  };

  if (!isVisible || !deletionStatus?.isPendingDeletion) {
    return null;
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const getDaysRemainingText = (days: number) => {
    if (days <= 0) {
      return 'Account will be permanently deleted soon';
    } else if (days === 1) {
      return '1 day remaining';
    } else {
      return `${days} days remaining`;
    }
  };

  const getBannerColor = (days: number) => {
    if (days <= 3) {
      return 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800';
    } else if (days <= 7) {
      return 'bg-orange-50 dark:bg-orange-900/20 border-orange-200 dark:border-orange-800';
    } else {
      return 'bg-yellow-50 dark:bg-yellow-900/20 border-yellow-200 dark:border-yellow-800';
    }
  };

  const getTextColor = (days: number) => {
    if (days <= 3) {
      return 'text-red-800 dark:text-red-200';
    } else if (days <= 7) {
      return 'text-orange-800 dark:text-orange-200';
    } else {
      return 'text-yellow-800 dark:text-yellow-200';
    }
  };

  const getIconColor = (days: number) => {
    if (days <= 3) {
      return 'text-red-500';
    } else if (days <= 7) {
      return 'text-orange-500';
    } else {
      return 'text-yellow-500';
    }
  };

  return (
    <div className={`border rounded-lg p-4 mb-6 ${getBannerColor(deletionStatus.daysRemaining)}`}>
      <div className="flex items-start">
        <AlertTriangle className={`w-5 h-5 mr-3 mt-0.5 flex-shrink-0 ${getIconColor(deletionStatus.daysRemaining)}`} />
        
        <div className="flex-1 min-w-0">
          <h3 className={`font-medium ${getTextColor(deletionStatus.daysRemaining)} mb-1`}>
            Account Scheduled for Deletion
          </h3>
          
          <div className={`text-sm ${getTextColor(deletionStatus.daysRemaining)} space-y-1`}>
            <div className="flex items-center">
              <Clock className="w-4 h-4 mr-1" />
              <span className="font-medium">
                {getDaysRemainingText(deletionStatus.daysRemaining)}
              </span>
            </div>
            
            {deletionStatus.deletedAt && (
              <p>
                Deletion requested: {formatDate(deletionStatus.deletedAt)}
              </p>
            )}
            
            {deletionStatus.permanentDeletionDate && (
              <p>
                Permanent deletion: {formatDate(deletionStatus.permanentDeletionDate)}
              </p>
            )}
            
            <p className="mt-2">
              You can still cancel this action and restore your account. All your data will be preserved.
            </p>
          </div>

          <div className="flex items-center space-x-3 mt-3">
            <Button
              variant="outline"
              size="sm"
              onClick={handleCancelDeletion}
              disabled={isCancelling || isLoading}
              isLoading={isCancelling}
            >
              Cancel Deletion
            </Button>
            
            <button
              onClick={handleDismiss}
              className={`text-sm ${getTextColor(deletionStatus.daysRemaining)} hover:underline`}
            >
              Dismiss
            </button>
          </div>
        </div>

        <button
          onClick={handleDismiss}
          className={`ml-3 ${getTextColor(deletionStatus.daysRemaining)} hover:opacity-70`}
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
};
